/*
 * /appointment/$patientId (page)
 */
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import type { DateSelectArg, EventClickArg, EventInput } from "@fullcalendar/core"
import {
	Button,
	cn,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	InputFormField,
	Label,
	Page,
	Placeholder,
	SelectFormField,
	TextareaFormField,
	toast,
} from "@emr/react-ui"
import type { Appointment, Practitioner } from "@emr/db/server"
import { createFileRoute } from "@tanstack/react-router"
import {
	CalendarClock,
	Loader2,
	Plus,
	Trash2,
} from "lucide-react"
import { useMemo, useRef, useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import { PatientHeader } from "../../features/patient/patient-header"
import type { Patient } from "@emr/db/client"
import { useAuth } from "@emr/auth/client"
import { isAdmin } from "@emr/utils"

export const Route = createFileRoute("/appointment/$patientId")({
	component: PatientAppointmentsPage,
})

/* ─── Constants ──────────────────────────────────────────────────────────── */

const APPOINTMENT_TYPES = [
	{ value: "initial", label: "Initial Consultation" },
	{ value: "follow_up", label: "Follow-Up" },
	{ value: "procedure", label: "Procedure" },
	{ value: "phone", label: "Phone Call" },
	{ value: "other", label: "Other" },
]

const APPOINTMENT_STATUSES = [
	{ value: "scheduled", label: "Scheduled" },
	{ value: "confirmed", label: "Confirmed" },
	{ value: "cancelled", label: "Cancelled" },
	{ value: "completed", label: "Completed" },
	{ value: "no_show", label: "No Show" },
]

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
	scheduled: { label: "Scheduled", className: "bg-blue-100 text-blue-700 border-blue-200" },
	confirmed: { label: "Confirmed", className: "bg-green-100 text-green-700 border-green-200" },
	cancelled: { label: "Cancelled", className: "bg-red-100 text-red-600 border-red-200" },
	completed: { label: "Completed", className: "bg-gray-100 text-gray-600 border-gray-200" },
	no_show: { label: "No Show", className: "bg-amber-100 text-amber-700 border-amber-200" },
}

const STATUS_COLORS: Record<string, string> = {
	scheduled: "#3b82f6",
	confirmed: "#10b981",
	cancelled: "#ef4444",
	completed: "#6b7280",
	no_show: "#f59e0b",
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function formatApptTime(startsAt: Date | string, endsAt: Date | string, tz: string): string {
	const s = new Date(startsAt as any)
	const e = new Date(endsAt as any)
	const date = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz,
		weekday: "short",
		month: "short",
		day: "numeric",
		year: "numeric",
	}).format(s)
	const start = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz,
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	}).format(s)
	const end = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz,
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
		timeZoneName: "short",
	}).format(e)
	return `${date} · ${start} – ${end}`
}

function utcToDisplayDate(utcInput: Date | string, tz: string): Date {
	const utc = new Date(utcInput as any)
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone: tz,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
	}).formatToParts(utc)
	const get = (t: string) =>
		parseInt(parts.find((p) => p.type === t)?.value ?? "0", 10)
	const h = get("hour")
	return new Date(
		get("year"),
		get("month") - 1,
		get("day"),
		h % 24,
		get("minute"),
		get("second"),
	)
}

function utcToLocalParts(utcDate: Date | string, tz: string): { date: string; time: string } {
	const d = new Date(utcDate as any)
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone: tz,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	}).formatToParts(d)
	const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ""
	const h = parseInt(get("hour"), 10) % 24
	return {
		date: `${get("year")}-${get("month")}-${get("day")}`,
		time: `${String(h).padStart(2, "0")}:${get("minute")}`,
	}
}

function localToUTC(dateStr: string, timeStr: string, tz: string): Date {
	const naive = new Date(`${dateStr}T${timeStr}:00.000Z`)
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone: tz,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
	}).formatToParts(naive)
	const get = (t: string) =>
		parseInt(parts.find((p) => p.type === t)?.value ?? "0", 10)
	const tzAsUTC = Date.UTC(
		get("year"),
		get("month") - 1,
		get("day"),
		get("hour") % 24,
		get("minute"),
		get("second"),
	)
	return new Date(naive.getTime() + (naive.getTime() - tzAsUTC))
}

function addMinutes(timeStr: string, minutes: number): string {
	const [h, m] = timeStr.split(":").map(Number)
	const total = h * 60 + m + minutes
	return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`
}

/* ─── Schemas ────────────────────────────────────────────────────────────── */

const bookSchema = z.object({
	startTime: z.string().min(1, "Required"),
	endTime: z.string().min(1, "Required"),
	type: z.string().optional(),
	status: z.string().default("scheduled"),
	reason: z.string().optional(),
	notes: z.string().optional(),
})
type BookValues = z.infer<typeof bookSchema>

const editStatusSchema = z.object({
	status: z.string().min(1, "Required"),
	notes: z.string().optional(),
})
type EditStatusValues = z.infer<typeof editStatusSchema>

/* ─── Pending slot type ──────────────────────────────────────────────────── */

type PendingSlot = {
	availabilityId: string
	practitionerId: string
	practitionerName: string
	startsAt: Date
	endsAt: Date
	timezone: string
	defaultAppointmentMinutes: number | null
	preselectedStart?: string  // HH:MM in slot timezone, set by drag-select
	preselectedEnd?: string
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

const MOCK_PATIENT: Partial<Patient> = { firstName: "...", lastName: "" }

function PatientAppointmentsPage() {
	const { patientId } = Route.useParams()
	const { user } = useAuth()
	const userIsAdmin = isAdmin(user)

	const [newApptOpen, setNewApptOpen] = useState(false)
	const [editAppt, setEditAppt] = useState<Appointment | null>(null)
	const [deleteAppt, setDeleteAppt] = useState<Appointment | null>(null)

	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: { id: patientId } })
	const displayPatient = (patient ?? MOCK_PATIENT) as Patient

	const { data: myUser } = trpc.user.getByFilter.useQuery(
		{ where: { id: user?.id ?? "" } },
		{ enabled: !!user?.id },
	)
	const displayTZ = myUser?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone

	const {
		data: appointments = [],
		isLoading,
		refetch,
	} = trpc.appointment.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const { data: practitioners = [] } = trpc.practitioner.getAllByFilter.useQuery(
		{ where: { active: true } },
	)

	const sortedAppts = useMemo(
		() =>
			[...(appointments ?? [])].sort(
				(a, b) =>
					new Date(b.startsAt as any).getTime() -
					new Date(a.startsAt as any).getTime(),
			),
		[appointments],
	)

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={false} />
			<Container width={"xl"} padding={"lg"} gap={"lg"}>
				<div className={"flex items-center justify-between"}>
					<h2 className={"text-lg font-semibold"}>Appointments</h2>
					{userIsAdmin && (
						<Button size={"sm"} onClick={() => setNewApptOpen(true)}>
							<Plus className={"mr-1.5 size-4"} />
							New Appointment
						</Button>
					)}
				</div>

				{isLoading ? (
					<Placeholder title={"Loading appointments…"} />
				) : sortedAppts.length === 0 ? (
					<Placeholder title={"No appointments"} />
				) : (
					<div className={"flex flex-col gap-3"}>
						{sortedAppts.map((appt) => {
							const prac = (practitioners ?? []).find((p) => p.id === appt.practitionerId)
							const badge = STATUS_BADGE[appt.status] ?? STATUS_BADGE.scheduled
							return (
								<div
									key={appt.id}
									className={"flex items-start justify-between gap-4 rounded-lg border bg-card px-5 py-4"}
								>
									<div className={"flex items-start gap-3"}>
										<CalendarClock className={"mt-0.5 size-5 shrink-0 text-muted-foreground"} />
										<div className={"flex flex-col gap-1"}>
											<p className={"text-sm font-medium"}>
												{formatApptTime(appt.startsAt, appt.endsAt, appt.timezone)}
											</p>
											{prac && (
												<p className={"text-xs text-muted-foreground"}>
													{prac.firstName} {prac.lastName}
													{prac.professionalDesignation ? `, ${prac.professionalDesignation}` : ""}
												</p>
											)}
											{appt.type && (
												<p className={"text-xs text-muted-foreground capitalize"}>
													{appt.type.replace("_", " ")}
												</p>
											)}
											{appt.reason && (
												<p className={"text-xs italic text-muted-foreground"}>
													"{appt.reason}"
												</p>
											)}
										</div>
									</div>
									<div className={"flex shrink-0 items-center gap-2"}>
										<span
											className={cn(
												"inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
												badge.className,
											)}
										>
											{badge.label}
										</span>
										{userIsAdmin && (
											<>
												<Button size={"sm"} variant={"outline"} onClick={() => setEditAppt(appt)}>
													Edit
												</Button>
												<Button
													size={"sm"}
													variant={"ghost"}
													className={"text-destructive hover:text-destructive"}
													onClick={() => setDeleteAppt(appt)}
												>
													<Trash2 className={"size-4"} />
												</Button>
											</>
										)}
									</div>
								</div>
							)
						})}
					</div>
				)}
			</Container>

			{userIsAdmin && (
				<>
					{newApptOpen && (
						<NewAppointmentDialog
							open={newApptOpen}
							onOpenChange={setNewApptOpen}
							practitioners={practitioners ?? []}
							displayTZ={displayTZ}
							patientId={patientId}
							patientName={`${displayPatient.firstName} ${displayPatient.lastName}`}
							onSuccess={() => { setNewApptOpen(false); refetch() }}
						/>
					)}

					{editAppt && (
						<EditStatusDialog
							appointment={editAppt}
							open={!!editAppt}
							onOpenChange={(o) => !o && setEditAppt(null)}
							onSuccess={() => { refetch(); setEditAppt(null) }}
						/>
					)}

					{deleteAppt && (
						<DeleteConfirmDialog
							appointment={deleteAppt}
							open={!!deleteAppt}
							onOpenChange={(o) => !o && setDeleteAppt(null)}
							onSuccess={() => { refetch(); setDeleteAppt(null) }}
						/>
					)}
				</>
			)}
		</Page>
	)
}

/* ─── New Appointment Dialog (split panel: calendar + form) ──────────────── */

function NewAppointmentDialog({
	open,
	onOpenChange,
	practitioners,
	displayTZ,
	patientId,
	patientName,
	onSuccess,
}: {
	open: boolean
	onOpenChange: (o: boolean) => void
	practitioners: Practitioner[]
	displayTZ: string
	patientId: string
	patientName: string
	onSuccess: () => void
}) {
	const calendarRef = useRef<FullCalendar>(null)
	const [selectedPracId, setSelectedPracId] = useState<string>(practitioners[0]?.id ?? "")
	// phase 1: week overview — click availability → zoom to day
	// phase 2: day view with 10-min slots — drag/click to select times
	const [phase, setPhase] = useState<"week" | "day">("week")
	const [focusedAvail, setFocusedAvail] = useState<any | null>(null)
	const [selectedSlot, setSelectedSlot] = useState<PendingSlot | null>(null)

	const selectedPrac = practitioners.find((p) => p.id === selectedPracId)

	const { data: availabilities = [] } = trpc.practitionerAvailability.getAllByFilter.useQuery(
		{ where: { active: true, practitionerId: selectedPracId } },
		{ enabled: !!selectedPracId },
	)

	const { data: appts = [] } = trpc.appointment.getAllByFilter.useQuery(
		{ where: { active: true, practitionerId: selectedPracId } },
		{ enabled: !!selectedPracId },
	)

	const pracLabel = selectedPrac
		? [selectedPrac.firstName, selectedPrac.lastName, selectedPrac.professionalDesignation]
				.filter(Boolean)
				.join(" ")
		: ""

	// In Phase 2, use the practitioner's timezone so the calendar, the selection,
	// and the booking form all show the same clock value. In Phase 1 use the
	// admin's display preference.
	const calendarTZ = phase === "day" && focusedAvail ? focusedAvail.timezone : displayTZ

	const events = useMemo<EventInput[]>(() => {
		const apptEvents: EventInput[] = (appts ?? []).map((ap) => ({
			id: `appt-${ap.id}`,
			start: utcToDisplayDate(ap.startsAt, calendarTZ),
			end: utcToDisplayDate(ap.endsAt, calendarTZ),
			backgroundColor: STATUS_COLORS[ap.status] ?? "#6b7280",
			borderColor: STATUS_COLORS[ap.status] ?? "#6b7280",
			textColor: "#ffffff",
			title: ap.status.replace("_", " "),
		}))

		if (phase === "week") {
			// Foreground events: visible, clickable, show practitioner name
			const availEvents: EventInput[] = (availabilities ?? []).map((a) => ({
				id: `avail-${a.id}`,
				start: utcToDisplayDate(a.startsAt, calendarTZ),
				end: utcToDisplayDate(a.endsAt, calendarTZ),
				backgroundColor: "#e0f2fe",
				borderColor: "#38bdf8",
				textColor: "#0369a1",
				title: pracLabel,
				editable: false,
				extendedProps: { availability: a },
			}))
			return [...availEvents, ...apptEvents]
		} else {
			// Day view: one background event for the focused availability (enables selectConstraint)
			const bgEvents: EventInput[] = focusedAvail
				? [
						{
							id: `avail-bg-${focusedAvail.id}`,
							groupId: "availability",
							display: "background",
							start: utcToDisplayDate(focusedAvail.startsAt, calendarTZ),
							end: utcToDisplayDate(focusedAvail.endsAt, calendarTZ),
							backgroundColor: "#bfdbfe",
						},
					]
				: []
			return [...bgEvents, ...apptEvents]
		}
	}, [availabilities, appts, calendarTZ, phase, focusedAvail, pracLabel])

	// Appointments on the same day as the focused availability (for the right panel)
	const apptsSameDayAsFocused = useMemo(() => {
		if (!focusedAvail) return []
		const date = utcToLocalParts(new Date(focusedAvail.startsAt), focusedAvail.timezone).date
		return (appts ?? []).filter(
			(a) =>
				utcToLocalParts(a.startsAt as any, focusedAvail.timezone).date === date &&
				a.status !== "cancelled",
		)
	}, [focusedAvail, appts])

	function handlePracSelect(pracId: string) {
		setSelectedPracId(pracId)
		setSelectedSlot(null)
		setFocusedAvail(null)
		if (phase === "day") {
			setPhase("week")
			calendarRef.current?.getApi()?.changeView("timeGridWeek")
		}
	}

	function buildSlot(availability: any, preselectedStart?: string, preselectedEnd?: string): PendingSlot {
		const pracName = selectedPrac
			? `${selectedPrac.firstName} ${selectedPrac.lastName}`
			: "Unknown"
		return {
			availabilityId: availability.id,
			practitionerId: availability.practitionerId,
			practitionerName: pracName,
			startsAt: new Date(availability.startsAt),
			endsAt: new Date(availability.endsAt),
			timezone: availability.timezone,
			defaultAppointmentMinutes: selectedPrac?.defaultAppointmentMinutes ?? null,
			preselectedStart,
			preselectedEnd,
		}
	}

	// Phase 1: clicking a foreground availability event → zoom to day view
	function handleEventClick(info: EventClickArg) {
		if (phase !== "week") return
		const { availability } = info.event.extendedProps
		if (!availability) return

		setFocusedAvail(availability)
		setPhase("day")
		const api = calendarRef.current?.getApi()
		if (api) {
			api.gotoDate(info.event.start!)
			api.changeView("timeGridDay")
		}
	}

	function handleBackToWeek() {
		setFocusedAvail(null)
		setSelectedSlot(null)
		setPhase("week")
		calendarRef.current?.getApi()?.changeView("timeGridWeek")
	}

	// Phase 2: drag or click-snap within availability → pre-fill booking form
	function handleSelect(selectInfo: DateSelectArg) {
		if (phase !== "day" || !focusedAvail) return
		const pad = (n: number) => String(n).padStart(2, "0")
		const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
		const toTimeStr = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`

		const selStartUTC = localToUTC(toDateStr(selectInfo.start), toTimeStr(selectInfo.start), focusedAvail.timezone)
		const selEndUTC = localToUTC(toDateStr(selectInfo.end), toTimeStr(selectInfo.end), focusedAvail.timezone)

		const preStart = utcToLocalParts(selStartUTC, focusedAvail.timezone).time
		const preEnd = utcToLocalParts(selEndUTC, focusedAvail.timezone).time

		setSelectedSlot(buildSlot(focusedAvail, preStart, preEnd))
	}

	function handleClose(o: boolean) {
		if (!o) {
			setSelectedSlot(null)
			setFocusedAvail(null)
			setPhase("week")
		}
		onOpenChange(o)
	}

	const focusedStart = focusedAvail
		? utcToLocalParts(new Date(focusedAvail.startsAt), focusedAvail.timezone)
		: null
	const focusedEnd = focusedAvail
		? utcToLocalParts(new Date(focusedAvail.endsAt), focusedAvail.timezone)
		: null

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className={"flex h-[90vh] max-w-[95vw] flex-col gap-3 overflow-hidden p-5"}>
				<DialogHeader className={"shrink-0"}>
					<DialogTitle>New Appointment — {patientName}</DialogTitle>
				</DialogHeader>

				{/* Practitioner filter */}
				<div className={"shrink-0"}>
					<Label className={"mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground"}>
						Practitioner / Nurse
					</Label>
					<div className={"flex flex-wrap gap-2"}>
						{practitioners.map((p) => (
							<button
								key={p.id}
								type={"button"}
								onClick={() => handlePracSelect(p.id)}
								className={cn(
									"rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
									selectedPracId === p.id
										? "border-primary bg-primary text-primary-foreground"
										: "border-input bg-background hover:bg-accent",
								)}
							>
								{p.firstName} {p.lastName}
								{p.professionalDesignation ? `, ${p.professionalDesignation}` : ""}
							</button>
						))}
					</div>
				</div>

				{/* Legend / phase hint */}
				<div className={"flex shrink-0 items-center gap-4 text-xs text-muted-foreground"}>
					<span className={"flex items-center gap-1.5"}>
						<span className={"inline-block size-3 rounded-sm border border-sky-400 bg-sky-100"} />
						{phase === "week" ? "Available — click to open day view" : "Available — click or drag to select times"}
					</span>
					<span className={"flex items-center gap-1.5"}>
						<span className={"inline-block size-3 rounded-sm bg-blue-500"} />
						Scheduled
					</span>
					<span className={"flex items-center gap-1.5"}>
						<span className={"inline-block size-3 rounded-sm bg-green-500"} />
						Confirmed
					</span>
					{phase === "day" && (
						<button
							type={"button"}
							onClick={handleBackToWeek}
							className={"ml-auto text-xs font-medium text-primary hover:underline"}
						>
							← Week view
						</button>
					)}
				</div>

				{/* Split panel: calendar + right panel */}
				<div className={"flex gap-4"} style={{ height: "calc(90vh - 195px)" }}>
					{/* Calendar */}
					<div className={"min-w-0 flex-1 overflow-hidden rounded-lg border bg-background p-2"}>
						<div className={"mb-1 flex items-center gap-1.5 text-xs text-muted-foreground"}>
							<span>Times in:</span>
							<span className={"rounded bg-muted px-1.5 py-0.5 font-mono font-medium"}>
								{calendarTZ}
							</span>
							{phase === "day" && focusedAvail && calendarTZ !== displayTZ && (
								<span className={"italic"}>(practitioner's timezone)</span>
							)}
						</div>
						<FullCalendar
							key={`${selectedPracId}-${calendarTZ}`}
							ref={calendarRef}
							plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
							initialView={"timeGridWeek"}
							headerToolbar={{
								left: "prev,next today",
								center: "title",
								right: phase === "week" ? "timeGridWeek,timeGridDay" : "",
							}}
							timeZone={"local"}
							slotMinTime={"06:00:00"}
							slotMaxTime={"22:00:00"}
							slotDuration={phase === "day" ? "00:10:00" : "00:30:00"}
							allDaySlot={false}
							nowIndicator
							selectable={phase === "day"}
							selectMirror={phase === "day"}
							selectConstraint={phase === "day" ? "availability" : undefined}
							height={"100%"}
							events={events}
							eventClick={handleEventClick}
							select={phase === "day" ? handleSelect : undefined}
							eventContent={(info) => {
								if (info.event.extendedProps?.availability) {
									return (
										<div
											className={"h-full overflow-hidden px-0.5 py-1"}
											style={{ writingMode: "vertical-lr", textOrientation: "upright" }}
											title={info.event.title}
										>
											<span className={"text-xs font-semibold leading-none"}>
												{info.event.title}
											</span>
										</div>
									)
								}
								return (
									<div className={"overflow-hidden px-1 text-xs leading-snug"}>
										<p className={"truncate font-medium"}>{info.event.title}</p>
										<p className={"truncate opacity-80"}>{info.timeText}</p>
									</div>
								)
							}}
						/>
					</div>

					{/* Right panel */}
					<div className={"w-72 shrink-0 overflow-y-auto"}>
						{selectedSlot ? (
							<BookingForm
								slot={selectedSlot}
								patientId={patientId}
								patientName={patientName}
								existingAppts={appts ?? []}
								onClear={() => setSelectedSlot(null)}
								onSuccess={onSuccess}
							/>
						) : focusedAvail ? (
							/* Phase 2: show availability info + booked times until time is selected */
							<div className={"flex flex-col gap-4"}>
								<div className={"rounded-md border bg-muted/40 px-3 py-3 text-sm"}>
									<p className={"font-semibold"}>
										{selectedPrac?.firstName} {selectedPrac?.lastName}
									</p>
									{selectedPrac?.professionalDesignation && (
										<p className={"text-xs text-muted-foreground"}>
											{selectedPrac.professionalDesignation}
										</p>
									)}
									<div className={"mt-2 border-t pt-2"}>
										<p className={"text-xs text-muted-foreground"}>Availability window</p>
										<p className={"font-medium"}>
											{focusedStart?.time} – {focusedEnd?.time}
										</p>
										<p className={"text-xs text-muted-foreground"}>{focusedAvail.timezone}</p>
									</div>
								</div>

								{apptsSameDayAsFocused.length > 0 && (
									<div className={"rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs"}>
										<p className={"mb-1 font-semibold text-amber-800"}>Already booked:</p>
										{apptsSameDayAsFocused.map((a) => {
											const s = utcToLocalParts(a.startsAt as any, focusedAvail.timezone).time
											const e = utcToLocalParts(a.endsAt as any, focusedAvail.timezone).time
											return (
												<p key={a.id} className={"text-amber-700"}>
													{s}–{e}{" "}
													<span className={"opacity-70"}>
														({STATUS_BADGE[a.status]?.label ?? a.status})
													</span>
												</p>
											)
										})}
									</div>
								)}

								<p className={"text-sm text-muted-foreground"}>
									Click or drag within the blue area to select appointment times. Times are shown in{" "}
									<span className={"font-medium text-foreground"}>{focusedAvail.timezone}</span>.
								</p>
							</div>
						) : (
							/* Phase 1: prompt */
							<div className={"flex h-full flex-col items-center justify-center gap-3 text-center"}>
								<CalendarClock className={"size-10 text-muted-foreground/40"} />
								<p className={"text-sm text-muted-foreground"}>
									Click an availability block to open day view and select a time
								</p>
							</div>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

/* ─── Booking Form (right panel of NewAppointmentDialog) ─────────────────── */

function BookingForm({
	slot,
	patientId,
	patientName,
	existingAppts,
	onClear,
	onSuccess,
}: {
	slot: PendingSlot
	patientId: string
	patientName: string
	existingAppts: Appointment[]
	onClear: () => void
	onSuccess: () => void
}) {
	const create = trpc.appointment.create.useMutation({
		onError: (e) => toast.error(e.message),
	})

	const slotStart = utcToLocalParts(slot.startsAt, slot.timezone)
	const slotEnd = utcToLocalParts(slot.endsAt, slot.timezone)

	const defaultEndTime = slot.defaultAppointmentMinutes
		? (() => {
				const candidate = addMinutes(slotStart.time, slot.defaultAppointmentMinutes)
				return candidate <= slotEnd.time ? candidate : slotEnd.time
			})()
		: slotEnd.time

	const windowLabel = new Intl.DateTimeFormat("en-CA", {
		timeZone: slot.timezone,
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
	}).format(slot.startsAt)

	// Existing appointments for this practitioner on the same date (for overlap check + display)
	const apptsSameDay = existingAppts.filter((a) => {
		const apptDate = utcToLocalParts(a.startsAt as any, slot.timezone).date
		return apptDate === slotStart.date && a.status !== "cancelled"
	})

	const formDefaults = useMemo<BookValues>(
		() => ({
			startTime: slot.preselectedStart ?? slotStart.time,
			endTime: slot.preselectedEnd ?? defaultEndTime,
			type: "",
			status: "scheduled",
			reason: "",
			notes: "",
		}),
		 
		[slot.preselectedStart, slot.preselectedEnd, slotStart.time, defaultEndTime],
	)

	async function handleSubmit(values: BookValues) {
		try {
			if (values.startTime >= values.endTime) {
				return "End time must be after start time"
			}

			const newStart = localToUTC(slotStart.date, values.startTime, slot.timezone)
			const newEnd = localToUTC(slotStart.date, values.endTime, slot.timezone)

			const conflict = apptsSameDay.find((a) => {
				const aStart = new Date(a.startsAt as any)
				const aEnd = new Date(a.endsAt as any)
				return newStart < aEnd && newEnd > aStart
			})

			if (conflict) {
				const cs = utcToLocalParts(conflict.startsAt as any, slot.timezone).time
				const ce = utcToLocalParts(conflict.endsAt as any, slot.timezone).time
				return `Overlaps with existing appointment at ${cs}–${ce}`
			}

			await create.mutateAsync({
				practitionerId: slot.practitionerId,
				availabilityId: slot.availabilityId,
				patientId,
				startsAt: newStart,
				endsAt: newEnd,
				timezone: slot.timezone,
				type: values.type || null,
				status: values.status,
				reason: values.reason || null,
				notes: values.notes || null,
			})
			toast.success("Appointment booked")
			onSuccess()
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	return (
		<div className={"flex flex-col gap-4"}>
			{/* Slot summary */}
			<div className={"rounded-md border bg-muted/40 px-3 py-2.5 text-sm"}>
				<p className={"font-medium"}>{windowLabel}</p>
				<p className={"text-xs text-muted-foreground"}>
					{slot.practitionerName} · Window {slotStart.time}–{slotEnd.time}
				</p>
				<p className={"text-xs text-muted-foreground"}>{slot.timezone}</p>
			</div>

			{/* Patient (locked) */}
			<div className={"rounded-md border bg-muted/20 px-3 py-2 text-sm"}>
				<p className={"text-xs text-muted-foreground"}>Patient</p>
				<p className={"font-medium"}>{patientName}</p>
			</div>

			{/* Already-booked times in this window */}
			{apptsSameDay.length > 0 && (
				<div className={"rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs"}>
					<p className={"mb-1 font-semibold text-amber-800"}>Already booked this day:</p>
					{apptsSameDay.map((a) => {
						const s = utcToLocalParts(a.startsAt as any, slot.timezone).time
						const e = utcToLocalParts(a.endsAt as any, slot.timezone).time
						const badge = STATUS_BADGE[a.status]
						return (
							<p key={a.id} className={"text-amber-700"}>
								{s}–{e}{" "}
								<span className={"opacity-70"}>({badge?.label ?? a.status})</span>
							</p>
						)
					})}
				</div>
			)}

			<Form<BookValues>
				schema={bookSchema}
				defaultValues={formDefaults}
				onSubmit={handleSubmit}
				className={"flex flex-col gap-3"}
			>
				<div className={"grid grid-cols-2 gap-3"}>
					<InputFormField
						name={"startTime"}
						label={"Start"}
						type={"time"}
						min={slotStart.time}
						max={slotEnd.time}
					/>
					<InputFormField
						name={"endTime"}
						label={"End"}
						type={"time"}
						min={slotStart.time}
						max={slotEnd.time}
					/>
				</div>
				<SelectFormField
					name={"type"}
					label={"Type"}
					options={APPOINTMENT_TYPES}
					placeholder={"Select type"}
				/>
				<SelectFormField
					name={"status"}
					label={"Status"}
					options={APPOINTMENT_STATUSES}
					placeholder={"Select status"}
				/>
				<InputFormField
					name={"reason"}
					label={"Reason"}
					placeholder={"Chief complaint…"}
				/>
				<TextareaFormField
					name={"notes"}
					label={"Notes"}
					placeholder={"Additional notes…"}
					rows={2}
				/>

				<div className={"flex gap-2 pt-1"}>
					<Button
						type={"button"}
						variant={"ghost"}
						size={"sm"}
						className={"text-muted-foreground"}
						onClick={onClear}
					>
						← Different slot
					</Button>
					<Button type={"submit"} disabled={create.isPending} className={"flex-1"}>
						{create.isPending && <Loader2 className={"mr-2 size-4 animate-spin"} />}
						Book
					</Button>
				</div>
			</Form>
		</div>
	)
}

/* ─── Edit Status Dialog ─────────────────────────────────────────────────── */

function EditStatusDialog({
	appointment,
	open,
	onOpenChange,
	onSuccess,
}: {
	appointment: Appointment
	open: boolean
	onOpenChange: (o: boolean) => void
	onSuccess: () => void
}) {
	const update = trpc.appointment.update.useMutation({
		onError: (e) => toast.error(e.message),
	})

	async function handleSubmit(values: EditStatusValues) {
		try {
			await update.mutateAsync({ id: appointment.id, status: values.status, notes: values.notes || null })
			toast.success("Appointment updated")
			onSuccess()
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className={"sm:max-w-sm"}>
				<DialogHeader>
					<DialogTitle>Update Appointment</DialogTitle>
					<p className={"text-sm text-muted-foreground"}>
						{formatApptTime(appointment.startsAt, appointment.endsAt, appointment.timezone)}
					</p>
				</DialogHeader>
				<Form<EditStatusValues>
					schema={editStatusSchema}
					defaultValues={{
						status: appointment.status,
						notes: (appointment.notes as string | undefined) ?? "",
					}}
					onSubmit={handleSubmit}
					className={"flex flex-col gap-4"}
				>
					<SelectFormField name={"status"} label={"Status"} options={APPOINTMENT_STATUSES} placeholder={"Select status"} />
					<TextareaFormField name={"notes"} label={"Notes"} placeholder={"Internal notes…"} rows={3} />
					<DialogFooter>
						<Button type={"submit"} disabled={update.isPending}>
							{update.isPending && <Loader2 className={"mr-2 size-4 animate-spin"} />}
							Save Changes
						</Button>
					</DialogFooter>
				</Form>
			</DialogContent>
		</Dialog>
	)
}

/* ─── Delete Confirm Dialog ──────────────────────────────────────────────── */

function DeleteConfirmDialog({
	appointment,
	open,
	onOpenChange,
	onSuccess,
}: {
	appointment: Appointment
	open: boolean
	onOpenChange: (o: boolean) => void
	onSuccess: () => void
}) {
	const softDelete = trpc.appointment.update.useMutation({
		onError: (e) => toast.error(e.message),
	})

	async function handleDelete() {
		try {
			await softDelete.mutateAsync({ id: appointment.id, active: false })
			toast.success("Appointment removed")
			onSuccess()
		} catch (e: any) {
			toast.error(e?.message ?? "An error occurred")
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className={"sm:max-w-sm"}>
				<DialogHeader>
					<DialogTitle>Remove Appointment?</DialogTitle>
					<p className={"text-sm text-muted-foreground"}>
						{formatApptTime(appointment.startsAt, appointment.endsAt, appointment.timezone)}
					</p>
				</DialogHeader>
				<p className={"text-sm"}>
					This will remove the appointment from the patient's record. This action cannot be undone.
				</p>
				<DialogFooter className={"gap-2"}>
					<Button variant={"outline"} onClick={() => onOpenChange(false)} disabled={softDelete.isPending}>
						Cancel
					</Button>
					<Button variant={"destructive"} onClick={handleDelete} disabled={softDelete.isPending}>
						{softDelete.isPending && <Loader2 className={"mr-2 size-4 animate-spin"} />}
						Remove
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
